import { CancelTokenSource } from 'axios';

import { CarrierSetting } from '@rygen/rygen-ui/model';

import {
  Carrier,
  CarrierContact,
  CarrierSearchFilter,
  CarrierSearchResult,
  Contact,
} from '@/model';

import { BaseHttpController } from './BaseHttpController';

// Normally we don't allow optional booleans, but the backend should
// gracefully handle missing query params and it keeps the default value simple
export type FindAllInContextParams = {
  /** Only return carriers with a valid email tender config */
  withEmailConfig?: boolean;
};

const endpoint = 'carrier';

export class CarrierController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async findAll(): Promise<Carrier[]> {
    const response = await this.http.get<Carrier[]>('all');
    return response?.data.map((val) => new Carrier(val));
  }

  async findById(
    id: number,
    cancelToken?: CancelTokenSource,
  ): Promise<Carrier> {
    const response = await this.http.get<Carrier>(`${id}`, {
      cancelToken: cancelToken?.token,
    });
    return new Carrier(response.data);
  }

  async findByScac(scac: string): Promise<Carrier> {
    const response = await this.http.get<Carrier>(`scac/${scac}`);
    return new Carrier(response.data);
  }

  async findCarrierSettingsForScac(scac: string): Promise<CarrierSetting> {
    const response = await this.http.get<CarrierSetting>(
      `${scac}/client-config`,
    );
    return new CarrierSetting(response.data);
  }

  async areCarrierDirectCredentialsConfiguredForScac(
    scac: string,
  ): Promise<boolean> {
    const response = await this.http.get<boolean>(
      `${scac}/carrier-direct-configured`,
    );
    return response.data;
  }

  /** @deprecated Unbounded fetch of every carrier. Use {@link search}. */
  async findAllInContext(
    params: FindAllInContextParams = {},
  ): Promise<Carrier[]> {
    const response = await this.http.get<Carrier[]>('', { params });
    return response.data.map((val) => new Carrier(val));
  }

  async findPrimaryContact(carrierId: number): Promise<Contact | undefined> {
    const response = await this.http.get<CarrierContact>(
      `${carrierId}/primary-contact`,
    );
    return response.data.contact
      ? new Contact(response.data.contact)
      : undefined;
  }

  async search(
    filter: CarrierSearchFilter,
    opts: {
      allCarriers?: boolean;
      hasEmailConfig?: boolean;
      cancelToken?: CancelTokenSource;
    } = {},
  ): Promise<CarrierSearchResult> {
    const response = await this.http.post<CarrierSearchResult>(
      'search',
      filter,
      {
        params: {
          allCarriers: opts.allCarriers,
          hasEmailConfig: opts.hasEmailConfig,
        },
        cancelToken: opts.cancelToken?.token,
      },
    );
    return new CarrierSearchResult(response.data);
  }
}
